import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { inventoryAPI } from '../../services';
import { toast } from 'react-toastify';
import { baseToPackageQty, packageQtyToBase, inputUnitLabel } from '../../utils/inventoryUnit';

const MATERIAL_UNITS = [
  { value: 'KG', label: 'KG (kilogram)' },
  { value: 'G', label: 'G (gram)' },
  { value: 'L', label: 'L (lít)' },
  { value: 'ML', label: 'ML (mililít)' },
  { value: 'CAI', label: 'Cái' },
  { value: 'GOI', label: 'Gói' },
  { value: 'HOP', label: 'Hộp' },
  { value: 'CHAI', label: 'Chai' },
  { value: 'LON', label: 'Lon' },
  { value: 'PIECE', label: 'Piece' },
];

const MATERIAL_CATEGORIES = [
  { value: 'FOOD', label: 'Thực phẩm' },
  { value: 'BEVERAGE', label: 'Đồ uống' },
  { value: 'PACKAGING', label: 'Bao bì' },
  { value: 'CLEANING', label: 'Vệ sinh' },
  { value: 'OTHER', label: 'Khác' },
];

const emptyForm = {
  name: '',
  type: 'RAW',
  unit: 'KG',
  category: '',
  min_stock_threshold: '',
  par_level: '',
  requires_stocktake: true,
  is_active: true,
  recipe_yield_quantity: '',
};

export default function MaterialFormModal({ show, onHide, material, onSuccess }) {
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (material) {
      setFormData({
        name: material.name || '',
        type: material.type || 'RAW',
        unit: material.unit || 'KG',
        category: material.category || '',
        // #232210: ô nhập theo quy cách đóng gói (vd "10 thùng"), không bắt gõ thẳng gam/ml —
        // quy đổi ngược ra base-unit lúc lưu, giữ nguyên đơn vị gốc trong CSDL.
        min_stock_threshold:
          material.min_stock_threshold != null
            ? String(baseToPackageQty(material.min_stock_threshold, material))
            : '',
        // #151257: định mức = mức CẦN ĐẠT sau khi mua bù, khác ngưỡng tối thiểu (mức báo động).
        par_level:
          material.par_level != null
            ? String(baseToPackageQty(material.par_level, material))
            : '',
        requires_stocktake: material.requires_stocktake !== false,
        is_active: material.is_active !== false,
        recipe_yield_quantity: material.recipe_yield_quantity ?? '',
      });
    } else {
      setFormData(emptyForm);
    }
  }, [material, show]);

  // NVL mới (chưa tạo) chưa có quy cách đóng gói nào — dùng thẳng đơn vị đang chọn trong form.
  const thresholdUnitLabel = material ? inputUnitLabel(material) : formData.unit;
  const thresholdBasePreview =
    formData.min_stock_threshold !== ''
      ? packageQtyToBase(Number(formData.min_stock_threshold), material ?? { unit: formData.unit })
      : null;
  const showBasePreview =
    thresholdBasePreview != null && material && thresholdUnitLabel !== (material.unit ?? '');
  const parLevelBasePreview =
    formData.par_level !== ''
      ? packageQtyToBase(Number(formData.par_level), material ?? { unit: formData.unit })
      : null;
  const showParLevelBasePreview =
    parLevelBasePreview != null && material && thresholdUnitLabel !== (material.unit ?? '');

  const handleChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Vui lòng nhập tên NVL');
      return;
    }
    if (formData.type === 'SEMI_FINISHED') {
      const yieldVal = Number(formData.recipe_yield_quantity);
      if (!formData.recipe_yield_quantity || yieldVal <= 0) {
        toast.error('BTP phải có số lượng yield > 0 (một mẻ tạo ra bao nhiêu đơn vị)');
        return;
      }
    }
    try {
      setLoading(true);
      const isBtp = formData.type === 'SEMI_FINISHED';
      const minStockThresholdBase =
        !isBtp && formData.min_stock_threshold !== ''
          ? Math.round(
              packageQtyToBase(Number(formData.min_stock_threshold), material ?? { unit: formData.unit }) * 1000,
            ) / 1000
          : undefined;
      const parLevelBase =
        !isBtp && formData.par_level !== ''
          ? Math.round(
              packageQtyToBase(Number(formData.par_level), material ?? { unit: formData.unit }) * 1000,
            ) / 1000
          : undefined;
      const data = {
        name: formData.name.trim(),
        type: formData.type,
        unit: formData.unit,
        category: formData.category || undefined,
        min_stock_threshold: minStockThresholdBase,
        par_level: parLevelBase,
        requires_stocktake: isBtp ? false : formData.requires_stocktake,
        is_active: formData.is_active,
        recipe_yield_quantity: isBtp && formData.recipe_yield_quantity !== '' ? Number(formData.recipe_yield_quantity) : undefined,
      };
      if (material) {
        await inventoryAPI.updateMaterial(material.id, data);
        toast.success('Đã cập nhật NVL');
      } else {
        await inventoryAPI.createMaterial(data);
        toast.success('Đã tạo NVL mới');
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.message || 'Không thể lưu NVL');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{material ? 'Sửa NVL' : 'Thêm NVL mới'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Loại vật liệu</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio" id="type-raw" label="Nguyên liệu thô"
                checked={formData.type === 'RAW'}
                onChange={() => handleChange('type', 'RAW')}
                disabled={!!material}
              />
              <Form.Check
                type="radio" id="type-btp" label="Bán thành phẩm (BTP)"
                checked={formData.type === 'SEMI_FINISHED'}
                onChange={() => handleChange('type', 'SEMI_FINISHED')}
                disabled={!!material}
              />
            </div>
            {formData.type === 'SEMI_FINISHED' && (
              <Form.Text className="text-warning fw-semibold">
                Đơn vị ảo — khi bán hệ thống tự trừ NVL thô theo công thức, không có tồn kho riêng.
              </Form.Text>
            )}
            {!!material && (
              <Form.Text className="text-muted">Không thể thay đổi loại sau khi tạo</Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Tên NVL <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="VD: Đường cát trắng"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Đơn vị <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={formData.unit}
              onChange={(e) => handleChange('unit', e.target.value)}
            >
              {MATERIAL_UNITS.map((u) => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Danh mục</Form.Label>
            <Form.Select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
            >
              <option value="">-- Không phân loại --</option>
              {MATERIAL_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {formData.type === 'SEMI_FINISHED' && (
            <Form.Group className="mb-3">
              <Form.Label>
                Sản lượng 1 mẻ (theo đơn vị đã chọn) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                min="0.001"
                step="0.001"
                value={formData.recipe_yield_quantity}
                onChange={(e) => handleChange('recipe_yield_quantity', e.target.value)}
                placeholder={`VD: 500${formData.unit ? ` (${formData.unit})` : ''}`}
                required
              />
              <Form.Text className="text-muted d-block mt-1">
                Khai báo 1 mẻ nấu/pha tạo ra bao nhiêu {formData.unit || 'đơn vị'}.{' '}
                <strong>VD:</strong> nếu pha 50g cà phê + 450ml nước → thu được 500ml nước cà phê,
                thì điền <strong>500</strong>. Khi bán 1 ly cần 100ml, hệ thống tự tính{' '}
                50g × (100 ÷ 500) = <strong>10g cà phê thô</strong> cần trừ.
              </Form.Text>
              <Form.Text className="text-muted d-block">
                Sau khi tạo, vào tab <strong>"Công thức BTP"</strong> để khai báo NVL thô cần cho 1 mẻ.
              </Form.Text>
            </Form.Group>
          )}

          {formData.type === 'RAW' && (
            <Form.Group className="mb-3">
              <Form.Label>Ngưỡng tồn kho tối thiểu ({thresholdUnitLabel || 'đơn vị'})</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.001"
                value={formData.min_stock_threshold}
                onChange={(e) => handleChange('min_stock_threshold', e.target.value)}
                placeholder={`Cảnh báo khi thấp hơn ngưỡng này (theo ${thresholdUnitLabel || 'đơn vị'})`}
              />
              {showBasePreview && (
                <Form.Text className="text-muted">
                  = {Number(thresholdBasePreview).toLocaleString('vi-VN', { maximumFractionDigits: 3 })} {material.unit}
                </Form.Text>
              )}
            </Form.Group>
          )}

          {formData.type === 'RAW' && (
            <Form.Group className="mb-3">
              <Form.Label>Định mức ({thresholdUnitLabel || 'đơn vị'})</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.001"
                value={formData.par_level}
                onChange={(e) => handleChange('par_level', e.target.value)}
                placeholder={`Mức cần đạt sau khi mua bù (theo ${thresholdUnitLabel || 'đơn vị'})`}
              />
              {showParLevelBasePreview && (
                <Form.Text className="text-muted">
                  = {Number(parLevelBasePreview).toLocaleString('vi-VN', { maximumFractionDigits: 3 })} {material.unit}
                </Form.Text>
              )}
            </Form.Group>
          )}

          {formData.type === 'RAW' && (
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                label="Cần kiểm kê cuối ca"
                checked={formData.requires_stocktake}
                onChange={(e) => handleChange('requires_stocktake', e.target.checked)}
              />
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              label="Đang hoạt động"
              checked={formData.is_active}
              onChange={(e) => handleChange('is_active', e.target.checked)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>Hủy</Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Đang lưu...' : material ? 'Cập nhật' : 'Tạo mới'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
